import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BadgeCheck, Eye, ShieldOff, ShieldCheck, History } from "lucide-react";

const STATUS_STYLES = {
  pending:    "bg-[#EFEADF] text-[#5C5C5C]",
  approved:   "bg-[color:var(--verified-soft)] text-[color:var(--verified)]",
  rejected:   "bg-[#FBEAE4] text-[color:var(--accent)]",
  expired:    "bg-[#FBEAE4] text-[color:var(--accent)]",
  superseded: "bg-[#EFEADF] text-[#5C5C5C]",
};

// One row per (vendor_id) — collapses the flat doc list into per-vendor cards
// so the new Revoke / Restore / History controls have a stable target.
function groupByVendor(all) {
  const map = new Map();
  for (const d of all) {
    const key = d.vendor_id;
    if (!map.has(key)) map.set(key, {
      vendor_id: key, vendor_name: d.vendor_name, vendor_category: d.vendor_category, docs: [],
    });
    map.get(key).docs.push(d);
  }
  return [...map.values()];
}

export default function AdminVerification() {
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [notes, setNotes] = useState("");
  const [fileData, setFileData] = useState(null);
  const [revokeVendor, setRevokeVendor] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [historyVendor, setHistoryVendor] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);

  const load = () => {
    api.get("/verification/admin/pending").then(({ data }) => setPending(data)).catch(() => {});
    api.get("/verification/admin/all").then(({ data }) => setAll(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openPreview = async (doc) => {
    setPreviewDoc(doc); setFileData(null);
    try {
      const { data } = await api.get(`/verification/documents/${doc.document_id}/file`);
      setFileData(data);
    } catch (e) { toast.error("Could not load file"); }
  };

  const submitReview = async (status) => {
    if (status === "rejected" && !notes.trim()) {
      toast.error("Rejection reason is required"); return;
    }
    try {
      await api.put(`/verification/admin/documents/${reviewDoc.document_id}/review`, { status, reviewer_notes: notes });
      toast.success(status === "approved" ? "Document approved" : "Document rejected");
      setReviewDoc(null); setNotes("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitRevoke = async () => {
    if (!revokeReason.trim()) { toast.error("Revocation reason is required"); return; }
    try {
      await api.post(`/verification/admin/vendors/${revokeVendor.vendor_id}/revoke`, { reason: revokeReason });
      toast.success("Verification revoked");
      setRevokeVendor(null); setRevokeReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const restoreVendor = async (vendor_id) => {
    try {
      const { data } = await api.post(`/verification/admin/vendors/${vendor_id}/restore`);
      toast.success(data.verified ? "Verification restored" : "Revocation cleared — vendor not currently eligible");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openHistory = async (vendor) => {
    setHistoryVendor(vendor); setHistoryEvents([]);
    try {
      const { data } = await api.get(`/verification/admin/vendors/${vendor.vendor_id}/history`);
      setHistoryEvents(data.events || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const effStatus = (d) => d.effective_status || d.status;

  const renderRow = (d) => (
    <div key={d.document_id} data-testid={`admin-doc-${d.document_id}`} className="rounded-xl border border-[color:var(--border)] p-4 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold truncate">{d.vendor_name}</span>
          <span className="text-xs text-[color:var(--text-muted)]">· {d.vendor_category}</span>
          <span data-testid={`doc-status-${d.document_id}`} className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${STATUS_STYLES[effStatus(d)] || STATUS_STYLES.pending}`}>{effStatus(d)}</span>
        </div>
        <div className="text-xs text-[color:var(--text-muted)] mt-1 truncate">
          {d.doc_type_label} · {d.file_name} · {new Date(d.uploaded_at).toLocaleDateString()}
          {d.expires_at && ` · expires ${new Date(d.expires_at).toLocaleDateString()}`}
        </div>
        {d.reviewer_notes && <div className="text-xs italic text-[color:var(--text-muted)] mt-1">"{d.reviewer_notes}"</div>}
      </div>
      <div className="flex gap-2 shrink-0">
        <Button data-testid={`preview-${d.document_id}`} variant="outline" onClick={() => openPreview(d)} className="rounded-lg h-9 border-[color:var(--border-strong)]">
          <Eye className="w-3.5 h-3.5 mr-1" /> Preview
        </Button>
        <Button data-testid={`review-${d.document_id}`} onClick={() => { setReviewDoc(d); setNotes(d.reviewer_notes || ""); }} className="btn-sage rounded-lg h-9">
          Review
        </Button>
      </div>
    </div>
  );

  const renderVendorCard = (v) => {
    const anyApproved = v.docs.some(d => d.status === "approved" && !d.superseded_by);
    // We can't tell revocation state from the doc list alone, so we key it off
    // any recent verification_revoked audit surfacing here — otherwise the
    // Restore button is always shown as a safe no-op when not revoked.
    return (
      <div key={v.vendor_id} data-testid={`admin-vendor-verify-${v.vendor_id}`} className="rounded-xl border border-[color:var(--border-strong)] p-4">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="font-semibold">{v.vendor_name}</div>
            <div className="text-xs text-[color:var(--text-muted)]">{v.vendor_category} · {v.docs.length} doc{v.docs.length === 1 ? "" : "s"}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid={`open-history-${v.vendor_id}`} variant="outline" onClick={() => openHistory(v)} className="rounded-lg h-9 border-[color:var(--border-strong)]">
              <History className="w-3.5 h-3.5 mr-1" /> History
            </Button>
            {anyApproved && (
              <Button data-testid={`revoke-${v.vendor_id}`} variant="outline" onClick={() => setRevokeVendor(v)} className="rounded-lg h-9 border-[color:var(--accent)] text-[color:var(--accent)]">
                <ShieldOff className="w-3.5 h-3.5 mr-1" /> Revoke
              </Button>
            )}
            <Button data-testid={`restore-${v.vendor_id}`} onClick={() => restoreVendor(v.vendor_id)} className="btn-sage rounded-lg h-9">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Restore
            </Button>
          </div>
        </div>
        <div className="space-y-2">{v.docs.map(renderRow)}</div>
      </div>
    );
  };

  const vendorGroups = groupByVendor(all);

  return (
    <div data-testid="admin-verification">
      <Tabs defaultValue="pending">
        <TabsList className="bg-[color:var(--surface)]">
          <TabsTrigger value="pending" data-testid="tab-pending-docs">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-docs">All documents</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-6 space-y-3">
          {pending.length === 0 ? (
            <div className="text-[color:var(--text-muted)] text-sm">No pending documents.</div>
          ) : pending.map(renderRow)}
        </TabsContent>
        <TabsContent value="all" className="mt-6 space-y-4">
          {vendorGroups.length === 0 ? (
            <div className="text-[color:var(--text-muted)] text-sm">No documents uploaded yet.</div>
          ) : vendorGroups.map(renderVendorCard)}
        </TabsContent>
      </Tabs>

      {/* Revoke verification dialog */}
      <Dialog open={!!revokeVendor} onOpenChange={(v) => !v && (setRevokeVendor(null), setRevokeReason(""))}>
        <DialogContent className="max-w-md bg-[color:var(--bg)]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Revoke verification</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[color:var(--text-muted)]">
            Removes the Verified badge for <span className="font-semibold text-[color:var(--text)]">{revokeVendor?.vendor_name}</span>. Documents remain on file. Requires a reason for the audit log.
          </div>
          <div className="mt-3">
            <div className="eyebrow mb-2">Reason (required)</div>
            <Textarea data-testid="revoke-reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} rows={3} className="bg-white" placeholder="e.g. Business license verified as fraudulent" />
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => (setRevokeVendor(null), setRevokeReason(""))} className="rounded-lg">Cancel</Button>
            <Button data-testid="confirm-revoke-btn" onClick={submitRevoke} disabled={!revokeReason.trim()} className="btn-sage rounded-lg">
              <ShieldOff className="w-4 h-4 mr-2" /> Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History drawer */}
      <Dialog open={!!historyVendor} onOpenChange={(v) => !v && setHistoryVendor(null)}>
        <DialogContent className="max-w-2xl bg-[color:var(--bg)]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Verification history</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[color:var(--text-muted)]">{historyVendor?.vendor_name} · {historyVendor?.vendor_category}</div>
          <div className="mt-3 max-h-[60vh] overflow-auto space-y-2" data-testid="history-events">
            {historyEvents.length === 0 ? (
              <div className="text-sm text-[color:var(--text-muted)] py-6 text-center">No events yet.</div>
            ) : historyEvents.map((e) => (
              <div key={e.event_id} className="rounded-lg border border-[color:var(--border)] p-3 text-sm">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <span className="font-medium">{e.event_type.replace(/_/g, " ")}</span>
                  <span className="text-xs text-[color:var(--text-muted)]">{new Date(e.timestamp).toLocaleString()}</span>
                </div>
                {e.reason && <div className="text-xs italic mt-1 text-[color:var(--text-muted)]">"{e.reason}"</div>}
                {e.metadata?.doc_type && <div className="text-xs mt-1">Doc: {e.metadata.doc_type}</div>}
                {e.actor_role && <div className="text-[10px] uppercase tracking-widest mt-1 text-[color:var(--accent)]">{e.actor_role}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(v) => !v && setPreviewDoc(null)}>
        <DialogContent className="max-w-3xl bg-[color:var(--bg)]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">{previewDoc?.doc_type_label}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[color:var(--text-muted)]">{previewDoc?.file_name}</div>
          <div className="mt-4 max-h-[65vh] overflow-auto rounded-lg border border-[color:var(--border)] bg-white p-2">
            {!fileData ? (
              <div className="p-8 text-center text-[color:var(--text-muted)]">Loading…</div>
            ) : fileData.file_type?.startsWith("image/") ? (
              <img
                src={`data:${fileData.file_type};base64,${fileData.file_data_b64}`}
                alt={fileData.file_name}
                className="w-full h-auto"
              />
            ) : (
              <iframe
                title={fileData.file_name}
                src={`data:${fileData.file_type};base64,${fileData.file_data_b64}`}
                className="w-full h-[60vh] border-0"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewDoc} onOpenChange={(v) => !v && setReviewDoc(null)}>
        <DialogContent className="max-w-md bg-[color:var(--bg)]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Review document</DialogTitle>
          </DialogHeader>
          <div className="text-sm">
            <div><span className="font-semibold">{reviewDoc?.vendor_name}</span> · {reviewDoc?.vendor_category}</div>
            <div className="text-[color:var(--text-muted)] mt-1">{reviewDoc?.doc_type_label} — {reviewDoc?.file_name}</div>
          </div>
          <div className="mt-4">
            <div className="eyebrow mb-2">Reviewer notes (visible to vendor)</div>
            <Textarea data-testid="review-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="bg-white" placeholder="Optional feedback for the vendor…" />
          </div>
          <DialogFooter className="mt-2">
            <Button data-testid="reject-doc-btn" variant="outline" onClick={() => submitReview("rejected")} disabled={!notes.trim()} className="rounded-lg border-[color:var(--accent)] text-[color:var(--accent)]">
              Reject
            </Button>
            <Button data-testid="approve-doc-btn" onClick={() => submitReview("approved")} className="btn-sage rounded-lg">
              <BadgeCheck className="w-4 h-4 mr-2" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
